import { styled } from '@openeducation/oe-rn-themes';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleProp, View, ViewStyle } from 'react-native';
import { useHover } from 'react-native-web-hooks';

import { OEText } from '../../../atoms/oe-text/oe-text';
import { RadioButton } from '../../../atoms/radio-button/radio-button';
import { flex, mr } from '../../../style-utils';

export interface ListItem<T> {
  value: T;
  label: string;
  description?: string;
}

export interface RadioListItemProp<T> extends ListItem<T> {
  type?: 'primary' | 'secondary';
  selected: boolean;
  onSelection: (value: T) => void;
  style?: StyleProp<ViewStyle>;
  testId?: string;
}

export const RadioListItem = <T extends unknown>({
  value,
  label,
  selected,
  description,
  onSelection,
  type,
  style,
  testId,
}: RadioListItemProp<T>) => {
  const viewRef = useRef<View>(null);
  const isHovered = useHover(viewRef);
  const { t } = useTranslation();
  return (
    <Container testID={testId} style={style} onPress={() => onSelection(value)}>
      <RadioButton
        hoveredApplied={isHovered}
        style={mr(8)}
        type={type}
        selected={selected}
        onPress={() => onSelection(value)}
      />
      <View style={flex('flex-1')} ref={viewRef}>
        <Label selected={selected} fontSize={14} fontType="bold">
          {t(label)}
        </Label>
        {description && <Description>{t(description)}</Description>}
      </View>
    </Container>
  );
};

const Container = styled.Pressable`
  flex-direction: row;
  width: 100%;
`;

const Description = styled(OEText).attrs(({ theme }) => ({
  fontSize: 12,
  lineHeight: 18,
  color: theme.color.dark3,
}))``;

const Label = styled(OEText).attrs<{ selected: boolean }>(({ selected }) => ({
  fontSize: 14,
  lineHeight: 18,
  fontType: selected ? 'semiBold' : 'default',
}))<{ selected: boolean }>``;
